import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Image,
  Modal as RNModal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import Svg, { Path } from "react-native-svg";

import { Skeleton } from "../../../components/ui";
import { COLORS, FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import type { AppRole } from "../../../lib/auth";
import {
  getCompanyMemberRegions,
  getMyCompanies,
  listCompanyMembers,
  removeMemberFromCompany,
  setMemberRole,
  type CompanyMember,
} from "../../../lib/companies";
import {
  inviteTeammate,
  listPendingCompanyInvites,
  resendCompanyInvite,
  revokeCompanyInvite,
  type CompanyInviteKind,
  type CompanyInviteRow,
} from "../../../lib/companyInvites";
import {
  createRecruitingTeam,
  deleteRecruitingTeam,
  listMyRecruitingTeams,
  setUserRecruitingTeam,
  summarizeScope,
  updateRecruitingTeam,
  type RecruitingTeam,
} from "../../../lib/recruitingTeams";
import { supabase } from "../../../lib/supabase";
import type { Company } from "../../../types/database";

const THEME = PANE_THEMES.Client;

const BLACK = THEME.pageBg;
const FOREST = THEME.sidebarBg;
const FOREST_MID = COLORS.FOREST_MID.hex;
const AMBER = THEME.accent;
const SAGE = COLORS.SAGE.hex;
const LINEN = THEME.textOnSurface;
const RED = THEME.destructive;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const TILE_BG = "rgba(28,43,26,0.40)";
const RED_BG = "rgba(220,80,80,0.08)";
const RED_BORDER = "rgba(220,80,80,0.40)";
const RED_TEXT = "rgba(255,180,180,0.92)";

const PAGE_MAX_W = 920;

type TabKey = "members" | "regions";

function isTabKey(s: unknown): s is TabKey {
  return s === "members" || s === "regions";
}

type CompanyRole = "client" | "manager" | "judge";
const COMPANY_ROLES: CompanyRole[] = ["client", "manager", "judge"];
const ROLE_LABEL: Record<CompanyRole, string> = {
  client: "Client",
  manager: "Manager",
  judge: "Judge",
};

function asCompanyRole(role: AppRole | null): CompanyRole | null {
  if (role === "client" || role === "manager" || role === "judge") return role;
  return null;
}

/* ─────────────────────────  Page  ───────────────────────── */

export default function ClientCompanyScreen() {
  const params = useLocalSearchParams();
  const tabParam = Array.isArray(params.tab) ? params.tab[0] : params.tab;
  const requestedTab: TabKey = isTabKey(tabParam) ? tabParam : "members";

  const [company, setCompany] = useState<Company | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUserName, setCurrentUserName] = useState<string | null>(null);

  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [pendingInvites, setPendingInvites] = useState<CompanyInviteRow[]>([]);
  const [regions, setRegions] = useState<RecruitingTeam[]>([]);
  const [memberRegionByUserId, setMemberRegionByUserId] = useState<Map<string, string | null>>(
    new Map(),
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingRegions, setLoadingRegions] = useState(true);

  const totalMemberCount = members.length + pendingInvites.length;
  // Regions tab hides until the user has at least one teammate — regions
  // only become useful once there's someone to group.
  const regionsAvailable = totalMemberCount > 1;
  const activeTab: TabKey = !regionsAvailable && requestedTab === "regions" ? "members" : requestedTab;

  useEffect(() => {
    let cancelled = false;
    void supabase.auth.getUser().then(({ data }) => {
      if (cancelled || !data.user) return;
      setCurrentUserId(data.user.id);
    });
    void getMyCompanies().then((list) => {
      if (!cancelled) setCompany(list[0] ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    void supabase
      .from("user_profiles")
      .select("full_name, email")
      .eq("id", currentUserId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled || !data) return;
        const name = (data as { full_name?: string | null; email?: string | null }).full_name
          ?? (data as { email?: string | null }).email
          ?? null;
        setCurrentUserName(name);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUserId]);

  const refreshMembers = useCallback(async () => {
    if (!company) return;
    setLoadingMembers(true);
    setLoadError(null);
    try {
      const [list, pending, regionsMap] = await Promise.all([
        listCompanyMembers(company.id),
        listPendingCompanyInvites(company.id),
        getCompanyMemberRegions(company.id),
      ]);
      setMembers(list);
      setPendingInvites(pending);
      setMemberRegionByUserId(regionsMap);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "Couldn't load members.");
    } finally {
      setLoadingMembers(false);
    }
  }, [company]);

  const refreshRegions = useCallback(async () => {
    setLoadingRegions(true);
    try {
      const list = await listMyRecruitingTeams();
      setRegions(list);
    } finally {
      setLoadingRegions(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (company) {
        void refreshMembers();
        void refreshRegions();
      }
    }, [company, refreshMembers, refreshRegions]),
  );

  function setTab(tab: TabKey) {
    router.setParams({ tab });
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: BLACK }} stickyHeaderIndices={[1]}>
      <View style={{ paddingHorizontal: 32, paddingTop: 28, maxWidth: PAGE_MAX_W }}>
        {/* Hero */}
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.displaySemiBold,
              fontSize: 10,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Overview
          </Text>
        </View>
        <Text
          style={{
            marginTop: 8,
            fontFamily: FONTS.displayLight,
            fontSize: 30,
            lineHeight: 36,
            color: LINEN,
          }}
        >
          Your company
        </Text>
        <Text
          style={{
            marginTop: 4,
            fontFamily: FONTS.bodyLight,
            fontSize: 13,
            color: WM,
          }}
        >
          {company?.name ?? "Your team"} · {totalMemberCount} {totalMemberCount === 1 ? "member" : "members"}
        </Text>
      </View>

      {/* Sticky tab bar */}
      <View
        style={[
          {
            marginTop: 22,
            paddingHorizontal: 32,
            backgroundColor: BLACK,
            borderBottomWidth: 1,
            borderBottomColor: BORDER,
            zIndex: 5,
          } as ViewStyle,
          Platform.OS === "web" ? ({ position: "sticky", top: 0 } as unknown as ViewStyle) : null,
        ]}
      >
        <View style={{ flexDirection: "row", gap: 6, maxWidth: PAGE_MAX_W }}>
          <TabButton label="Members" active={activeTab === "members"} onPress={() => setTab("members")} />
          {regionsAvailable ? (
            <TabButton label="Regions" active={activeTab === "regions"} onPress={() => setTab("regions")} />
          ) : null}
        </View>
      </View>

      {/* Body */}
      <View style={{ paddingHorizontal: 32, paddingVertical: 24, maxWidth: PAGE_MAX_W }}>
        {loadError ? <ErrorBanner message={loadError} /> : null}
        {activeTab === "members" ? (
          <MembersTab
            company={company}
            currentUserId={currentUserId}
            currentUserName={currentUserName}
            members={members}
            pendingInvites={pendingInvites}
            regions={regions}
            memberRegionByUserId={memberRegionByUserId}
            loading={loadingMembers}
            onChanged={refreshMembers}
          />
        ) : (
          <RegionsTab
            companyId={company?.id ?? null}
            regions={regions}
            loading={loadingRegions}
            onChanged={refreshRegions}
          />
        )}
      </View>
    </ScrollView>
  );
}

/* ─────────────────────────  Tab bar  ───────────────────────── */

function TabButton({
  label,
  active,
  onPress,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const web = Platform.OS === "web";
  return (
    <Pressable
      onPress={onPress}
      {...(web ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const) : {})}
      style={[
        {
          paddingHorizontal: 12,
          paddingVertical: 12,
          borderBottomWidth: 2,
          borderBottomColor: active ? AMBER : "transparent",
          marginBottom: -1,
        } as ViewStyle,
        web ? ({ cursor: "pointer", transition: "color 120ms" } as object) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyMedium,
          fontSize: 13,
          color: active ? LINEN : web && hover ? LINEN : WM,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

/* ─────────────────────────  Members tab  ───────────────────────── */

function MembersTab({
  company,
  currentUserId,
  currentUserName,
  members,
  pendingInvites,
  regions,
  memberRegionByUserId,
  loading,
  onChanged,
}: {
  company: Company | null;
  currentUserId: string | null;
  currentUserName: string | null;
  members: CompanyMember[];
  pendingInvites: CompanyInviteRow[];
  regions: RecruitingTeam[];
  memberRegionByUserId: Map<string, string | null>;
  loading: boolean;
  onChanged: () => Promise<void> | void;
}) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const regionsById = useMemo(() => {
    const m = new Map<string, RecruitingTeam>();
    for (const r of regions) m.set(r.id, r);
    return m;
  }, [regions]);

  const isAlone = members.length <= 1 && pendingInvites.length === 0;

  return (
    <View style={{ gap: 14 }}>
      <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
        <View style={{ flex: 1, minWidth: 0, paddingRight: 12 }}>
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: LINEN }}>
            Everyone with access to {company?.name ?? "your company"}
          </Text>
          <Text
            style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11.5, color: WM }}
          >
            <Text style={{ color: AMBER, fontFamily: FONTS.bodyBold }}>Client</Text> = full access ·{" "}
            <Text style={{ color: AMBER, fontFamily: FONTS.bodyBold }}>Manager</Text> = Coravu-managed program operator ·{" "}
            <Text style={{ color: AMBER, fontFamily: FONTS.bodyBold }}>Judge</Text> = scores submissions only
          </Text>
        </View>
        <PrimaryAmberButton
          label="+ Invite teammate"
          onPress={() => setInviteOpen(true)}
          disabled={!company}
        />
      </View>

      {rowError ? <ErrorBanner message={rowError} /> : null}

      {loading ? (
        <View style={{ gap: 8 }}>
          {[0, 1, 2].map((k) => <TeamSkeletonRow key={k} />)}
        </View>
      ) : isAlone ? (
        <EmptyCard
          title="Just you so far."
          subtitle="Click + Invite teammate to add your team — Clients to co-own programs, Managers to operate them, Judges to score submissions."
        />
      ) : (
        <View
          style={{
            backgroundColor: BLACK,
            borderWidth: 1,
            borderColor: BORDER,
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          {members.map((m, i) => (
            <MemberRow
              key={`m_${m.user_id}`}
              member={m}
              first={i === 0}
              isSelf={m.user_id === currentUserId}
              regions={regions}
              currentRegionId={memberRegionByUserId.get(m.user_id) ?? null}
              regionsById={regionsById}
              onError={(msg) => setRowError(msg)}
              onChanged={onChanged}
              companyId={company?.id ?? null}
              currentUserId={currentUserId}
            />
          ))}
          {pendingInvites.map((inv, i) => (
            <PendingInviteRow
              key={`p_${inv.id}`}
              invite={inv}
              first={members.length === 0 && i === 0}
              regions={regions}
              companyName={company?.name ?? ""}
              currentUserName={currentUserName}
              onChanged={onChanged}
              onError={(msg) => setRowError(msg)}
            />
          ))}
        </View>
      )}

      {company ? (
        <InviteTeammateModal
          visible={inviteOpen}
          company={company}
          currentUserName={currentUserName}
          regions={regions}
          onClose={() => setInviteOpen(false)}
          onDone={async () => {
            setInviteOpen(false);
            await onChanged();
          }}
        />
      ) : null}
    </View>
  );
}

/* ─────────────── Member row (existing member) ─────────────── */

function MemberRow({
  member,
  first,
  isSelf,
  regions,
  currentRegionId,
  regionsById,
  onError,
  onChanged,
  companyId,
  currentUserId,
}: {
  member: CompanyMember;
  first: boolean;
  isSelf: boolean;
  regions: RecruitingTeam[];
  currentRegionId: string | null;
  regionsById: Map<string, RecruitingTeam>;
  onError: (msg: string) => void;
  onChanged: () => Promise<void> | void;
  companyId: string | null;
  currentUserId: string | null;
}) {
  const [hover, setHover] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [removeArmed, setRemoveArmed] = useState(false);
  const [demoteConfirmOpen, setDemoteConfirmOpen] = useState<CompanyRole | null>(null);
  const web = Platform.OS === "web";

  const currentRole = asCompanyRole(member.app_role) ?? "client";

  const flashSaved = useCallback(() => {
    setSavedFlash(true);
    const t = setTimeout(() => setSavedFlash(false), 1800);
    return () => clearTimeout(t);
  }, []);

  async function commitRole(newRole: CompanyRole) {
    if (newRole === currentRole) return;
    const { error } = await setMemberRole(member.user_id, newRole);
    if (error) {
      onError(error.message);
      return;
    }
    flashSaved();
    await onChanged();
  }

  async function commitRegion(newRegionId: string | null) {
    if (newRegionId === currentRegionId) return;
    const { error } = await setUserRecruitingTeam(member.user_id, newRegionId);
    if (error) {
      onError(error.message);
      return;
    }
    flashSaved();
    await onChanged();
  }

  async function doRemove() {
    if (!companyId) return;
    const { error } = await removeMemberFromCompany(member.user_id, companyId);
    if (error) {
      onError(error.message);
      return;
    }
    setMenuOpen(false);
    await onChanged();
  }

  return (
    <View
      {...(web
        ? ({
            onMouseEnter: () => setHover(true),
            onMouseLeave: () => setHover(false),
          } as Record<string, () => void>)
        : {})}
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          paddingHorizontal: 16,
          paddingVertical: 14,
          borderTopWidth: first ? 0 : 1,
          borderTopColor: BORDER,
          backgroundColor: web && hover ? "rgba(181,135,58,0.04)" : "transparent",
        } as ViewStyle,
      ]}
    >
      <Avatar url={member.avatar_url} initials={member.initials} />

      <View style={{ flex: 1.6, minWidth: 0 }}>
        <Text
          style={{ fontFamily: FONTS.bodyMedium, fontSize: 14, color: LINEN }}
          numberOfLines={1}
        >
          {member.full_name ?? member.email ?? "Pending profile"}
          {isSelf ? (
            <Text style={{ fontFamily: FONTS.body, color: WL, fontSize: 11 }}>  · you</Text>
          ) : null}
        </Text>
        <Text
          style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11, color: WM }}
          numberOfLines={1}
        >
          {member.email ?? "—"}
        </Text>
      </View>

      <View style={{ width: 130 }}>
        <RoleDropdown
          value={currentRole}
          disabled={false}
          onChange={(next) => {
            // Self-demotion confirm — once they leave 'client' they can't get
            // back in without another client teammate (or admin support).
            if (isSelf && currentRole === "client" && next !== "client") {
              setDemoteConfirmOpen(next);
              return;
            }
            void commitRole(next);
          }}
        />
      </View>

      <View style={{ width: 150 }}>
        <RegionDropdown
          value={currentRegionId}
          regions={regions}
          onChange={(id) => void commitRegion(id)}
          disabled={regions.length === 0}
        />
      </View>

      <View style={{ width: 80, alignItems: "flex-end" }}>
        {savedFlash ? (
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 10.5, color: SAGE }}>
            Saved · just now
          </Text>
        ) : (
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 10.5,
              color: WL,
            }}
          >
            {regionsById.get(currentRegionId ?? "")?.name ? "" : ""}
          </Text>
        )}
      </View>

      {isSelf ? (
        <View style={{ width: 22, height: 22 }} />
      ) : (
        <Pressable
          onPress={() => setMenuOpen(true)}
          style={[
            { paddingHorizontal: 6, paddingVertical: 6 },
            web ? ({ cursor: "pointer" } as unknown as ViewStyle) : null,
          ]}
          accessibilityLabel="Member options"
        >
          <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
            <Path d="M7 3.5h.01M7 7h.01M7 10.5h.01" stroke={WM} strokeWidth={2} strokeLinecap="round" />
          </Svg>
        </Pressable>
      )}

      {/* Member ⋮ menu */}
      <CompactMenuModal
        visible={menuOpen}
        onClose={() => {
          setMenuOpen(false);
          setRemoveArmed(false);
        }}
        title={member.full_name ?? member.email ?? "Member"}
      >
        {removeArmed ? (
          <View style={{ gap: 8 }}>
            <Text style={{ fontFamily: FONTS.body, fontSize: 12.5, color: WS, lineHeight: 18 }}>
              Remove {member.full_name ?? member.email} from your company? They'll lose access immediately. This doesn't delete their Coravu account.
            </Text>
            <View style={{ flexDirection: "row", gap: 8, justifyContent: "flex-end" }}>
              <SecondaryButton label="Cancel" onPress={() => setRemoveArmed(false)} />
              <DangerButton label="Yes, remove" onPress={doRemove} />
            </View>
          </View>
        ) : (
          <View style={{ gap: 4 }}>
            <MenuRow label="Remove from company" tone="danger" onPress={() => setRemoveArmed(true)} />
          </View>
        )}
      </CompactMenuModal>

      {/* Self-demotion confirm */}
      <RNModal
        visible={demoteConfirmOpen !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setDemoteConfirmOpen(null)}
      >
        <View
          style={{
            flex: 1,
            backgroundColor: "rgba(0,0,0,0.65)",
            justifyContent: "center",
            paddingHorizontal: 16,
          }}
        >
          <View
            style={{
              maxWidth: 460,
              width: "100%",
              alignSelf: "center",
              backgroundColor: FOREST_MID,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: BORDER,
              padding: 22,
            }}
          >
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
              Heads up
            </Text>
            <Text
              style={{
                marginTop: 8,
                fontFamily: FONTS.body,
                fontSize: 13,
                color: WS,
                lineHeight: 20,
              }}
            >
              Once you demote yourself, you'll lose Client-level access to {/* */}
              {member.full_name ? member.full_name.split(" ")[0] : "your"} company.
              You won't be able to invite teammates or edit cases until another Client adds you back.
            </Text>
            <View style={{ marginTop: 18, flexDirection: "row", justifyContent: "flex-end", gap: 10 }}>
              <SecondaryButton label="Cancel" onPress={() => setDemoteConfirmOpen(null)} />
              <DangerButton
                label="Yes, continue"
                onPress={() => {
                  const next = demoteConfirmOpen;
                  setDemoteConfirmOpen(null);
                  if (next) void commitRole(next);
                }}
              />
            </View>
          </View>
        </View>
      </RNModal>
      {void currentUserId}
    </View>
  );
}

/* ─────────────── Pending invite row ─────────────── */

function PendingInviteRow({
  invite,
  first,
  regions,
  companyName,
  currentUserName,
  onChanged,
  onError,
}: {
  invite: CompanyInviteRow;
  first: boolean;
  regions: RecruitingTeam[];
  companyName: string;
  currentUserName: string | null;
  onChanged: () => Promise<void> | void;
  onError: (msg: string) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [resending, setResending] = useState(false);
  const [revokeArmed, setRevokeArmed] = useState(false);
  const region = regions.find((r) => r.id === invite.region_id) ?? null;

  async function doResend() {
    setResending(true);
    const { error } = await resendCompanyInvite(invite, companyName, currentUserName ?? undefined);
    setResending(false);
    if (error) {
      onError(error.message);
      return;
    }
    setMenuOpen(false);
    await onChanged();
  }

  async function doRevoke() {
    const { error } = await revokeCompanyInvite(invite.id);
    if (error) {
      onError(error.message);
      return;
    }
    setMenuOpen(false);
    setRevokeArmed(false);
    await onChanged();
  }

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingHorizontal: 16,
        paddingVertical: 14,
        borderTopWidth: first ? 0 : 1,
        borderTopColor: BORDER,
        backgroundColor: "rgba(181,135,58,0.03)",
      }}
    >
      <Avatar url={null} initials="…" tint="amber" />
      <View style={{ flex: 1.6, minWidth: 0 }}>
        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 14, color: LINEN }} numberOfLines={1}>
          {invite.email}
        </Text>
        <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11, color: WM }} numberOfLines={1}>
          Invited as {ROLE_LABEL[asCompanyRole((invite.role as AppRole) ?? null) ?? "client"]}
          {region ? ` · ${region.name}` : ""}
        </Text>
      </View>
      <View style={{ width: 130 }}>
        <PendingPill label="Pending" />
      </View>
      <View style={{ width: 150 }} />
      <View style={{ width: 80, alignItems: "flex-end" }}>
        <Text style={{ fontFamily: FONTS.body, fontSize: 10.5, color: WL }}>
          Expires {fmtRelativeDate(invite.expires_at)}
        </Text>
      </View>
      <Pressable
        onPress={() => setMenuOpen(true)}
        style={[
          { paddingHorizontal: 6, paddingVertical: 6 },
          Platform.OS === "web" ? ({ cursor: "pointer" } as unknown as ViewStyle) : null,
        ]}
        accessibilityLabel="Invite options"
      >
        <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
          <Path d="M7 3.5h.01M7 7h.01M7 10.5h.01" stroke={WM} strokeWidth={2} strokeLinecap="round" />
        </Svg>
      </Pressable>

      <CompactMenuModal
        visible={menuOpen}
        onClose={() => {
          setMenuOpen(false);
          setRevokeArmed(false);
        }}
        title={invite.email}
      >
        {revokeArmed ? (
          <View style={{ gap: 8 }}>
            <Text style={{ fontFamily: FONTS.body, fontSize: 12.5, color: WS, lineHeight: 18 }}>
              Revoke this invite? {invite.email} won't be able to use the link they received.
            </Text>
            <View style={{ flexDirection: "row", gap: 8, justifyContent: "flex-end" }}>
              <SecondaryButton label="Cancel" onPress={() => setRevokeArmed(false)} />
              <DangerButton label="Yes, revoke" onPress={doRevoke} />
            </View>
          </View>
        ) : (
          <View style={{ gap: 4 }}>
            <MenuRow
              label={resending ? "Resending…" : "Resend invite"}
              onPress={() => void doResend()}
              disabled={resending}
            />
            <MenuRow label="Revoke invite" tone="danger" onPress={() => setRevokeArmed(true)} />
          </View>
        )}
      </CompactMenuModal>
    </View>
  );
}

function fmtRelativeDate(iso: string): string {
  const target = new Date(iso).getTime();
  const days = Math.round((target - Date.now()) / 86400000);
  if (days < 0) return "expired";
  if (days === 0) return "today";
  if (days === 1) return "in 1d";
  return `in ${days}d`;
}

/* ─────────────── Member primitives ─────────────── */

function Avatar({
  url,
  initials,
  tint,
}: {
  url: string | null;
  initials: string;
  tint?: "amber";
}) {
  return (
    <View
      style={{
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: tint === "amber" ? "rgba(181,135,58,0.18)" : FOREST_MID,
        borderWidth: 1,
        borderColor: "rgba(181,135,58,0.22)",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {url ? (
        <Image source={{ uri: url }} style={{ width: 36, height: 36 }} resizeMode="cover" />
      ) : (
        <Text style={{ fontFamily: FONTS.displaySemiBold, fontSize: 14, color: AMBER }}>
          {initials}
        </Text>
      )}
    </View>
  );
}

function PendingPill({ label }: { label: string }) {
  return (
    <View
      style={{
        alignSelf: "flex-start",
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: "rgba(181,135,58,0.45)",
        backgroundColor: "rgba(181,135,58,0.10)",
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: AMBER,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

/* ─────────────── Inline dropdowns ─────────────── */

function RoleDropdown({
  value,
  onChange,
  disabled,
}: {
  value: CompanyRole;
  onChange: (v: CompanyRole) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <View>
      <DropdownTrigger
        label={ROLE_LABEL[value]}
        onPress={() => !disabled && setOpen(true)}
        disabled={disabled}
      />
      <CompactMenuModal visible={open} onClose={() => setOpen(false)} title="Set role">
        {COMPANY_ROLES.map((r) => (
          <MenuRow
            key={r}
            label={`${ROLE_LABEL[r]}${r === value ? "  · selected" : ""}`}
            onPress={() => {
              setOpen(false);
              onChange(r);
            }}
            highlight={r === value}
          />
        ))}
      </CompactMenuModal>
    </View>
  );
}

function RegionDropdown({
  value,
  regions,
  onChange,
  disabled,
}: {
  value: string | null;
  regions: RecruitingTeam[];
  onChange: (v: string | null) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selected = regions.find((r) => r.id === value) ?? null;
  return (
    <View>
      <DropdownTrigger
        label={selected ? selected.name : "All regions"}
        onPress={() => !disabled && setOpen(true)}
        disabled={disabled}
      />
      <CompactMenuModal visible={open} onClose={() => setOpen(false)} title="Set region">
        <MenuRow
          label="All regions"
          onPress={() => {
            setOpen(false);
            onChange(null);
          }}
          highlight={value === null}
        />
        {regions.map((r) => (
          <MenuRow
            key={r.id}
            label={r.name}
            onPress={() => {
              setOpen(false);
              onChange(r.id);
            }}
            highlight={r.id === value}
          />
        ))}
        {regions.length === 0 ? (
          <Text style={{ paddingHorizontal: 4, paddingVertical: 6, color: WM, fontSize: 12 }}>
            No regions yet — create one on the Regions tab.
          </Text>
        ) : null}
      </CompactMenuModal>
    </View>
  );
}

function DropdownTrigger({
  label,
  onPress,
  disabled,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={[
        {
          paddingHorizontal: 10,
          paddingVertical: 7,
          borderRadius: 4,
          borderWidth: 1,
          borderColor: BORDER,
          backgroundColor: "rgba(255,255,255,0.03)",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          opacity: disabled ? 0.5 : 1,
        } as ViewStyle,
        Platform.OS === "web"
          ? ({ cursor: disabled ? "default" : "pointer" } as unknown as ViewStyle)
          : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyMedium,
          fontSize: 12,
          color: LINEN,
          flex: 1,
        }}
        numberOfLines={1}
      >
        {label}
      </Text>
      <Text style={{ marginLeft: 6, color: WM, fontSize: 10 }}>▾</Text>
    </Pressable>
  );
}

/* ─────────────── Compact menu modal (used by ⋮ + dropdowns) ─────────────── */

function CompactMenuModal({
  visible,
  onClose,
  title,
  children,
}: {
  visible: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <RNModal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.55)",
          justifyContent: "center",
          paddingHorizontal: 16,
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{
            maxWidth: 360,
            width: "100%",
            alignSelf: "center",
            backgroundColor: FOREST_MID,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: BORDER,
            padding: 14,
          }}
        >
          {title ? (
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 10,
                letterSpacing: 1.6,
                textTransform: "uppercase",
                color: WM,
                marginBottom: 8,
              }}
              numberOfLines={1}
            >
              {title}
            </Text>
          ) : null}
          {children}
        </Pressable>
      </Pressable>
    </RNModal>
  );
}

function MenuRow({
  label,
  onPress,
  tone,
  highlight,
  disabled,
}: {
  label: string;
  onPress: () => void;
  tone?: "danger";
  highlight?: boolean;
  disabled?: boolean;
}) {
  const color = tone === "danger" ? RED : highlight ? AMBER : LINEN;
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={[
        {
          paddingHorizontal: 8,
          paddingVertical: 9,
          borderRadius: 4,
          opacity: disabled ? 0.5 : 1,
        } as ViewStyle,
        Platform.OS === "web"
          ? ({ cursor: disabled ? "default" : "pointer" } as unknown as ViewStyle)
          : null,
      ]}
    >
      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color }}>{label}</Text>
    </Pressable>
  );
}

/* ─────────────────────────  Regions tab  ───────────────────────── */

function RegionsTab({
  companyId,
  regions,
  loading,
  onChanged,
}: {
  companyId: string | null;
  regions: RecruitingTeam[];
  loading: boolean;
  onChanged: () => Promise<void> | void;
}) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<RecruitingTeam | null>(null);

  return (
    <View style={{ gap: 14 }}>
      <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
        <View style={{ flex: 1, minWidth: 0, paddingRight: 12 }}>
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: LINEN }}>
            Regions group your teammates by territory or business unit
          </Text>
          <Text
            style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11.5, color: WM }}
          >
            Most companies don't need them — add one only if you want to scope cases or managers by location.
          </Text>
        </View>
        <PrimaryAmberButton
          label="+ New region"
          onPress={() => {
            setEditing(null);
            setEditorOpen(true);
          }}
          disabled={!companyId}
        />
      </View>

      {loading ? (
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 12 }}>
          <RegionSkeletonCard />
          <RegionSkeletonCard />
        </View>
      ) : regions.length === 0 ? (
        <EmptyCard
          title="No regions yet."
          subtitle="Create one to scope a manager (or batch of managers) to specific schools, states, or business units."
        />
      ) : (
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 12 }}>
          {regions.map((t) => (
            <RegionCard
              key={t.id}
              team={t}
              onEdit={() => {
                setEditing(t);
                setEditorOpen(true);
              }}
            />
          ))}
        </View>
      )}

      {companyId ? (
        <RegionEditorModal
          visible={editorOpen}
          companyId={companyId}
          initial={editing}
          onClose={() => setEditorOpen(false)}
          onSaved={async () => {
            setEditorOpen(false);
            await onChanged();
          }}
        />
      ) : null}
    </View>
  );
}

function RegionCard({
  team,
  onEdit,
}: {
  team: RecruitingTeam;
  onEdit: () => void;
}) {
  return (
    <View
      style={{
        flexBasis: ("calc(50% - 6px)" as unknown) as number,
        flexGrow: 1,
        minWidth: 280,
        padding: 16,
        backgroundColor: BLACK,
        borderWidth: 1,
        borderColor: BORDER,
        borderRadius: 4,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <View
          style={{
            width: 12,
            height: 12,
            borderRadius: 6,
            backgroundColor: team.color || AMBER,
          }}
        />
        <Text
          style={{ flex: 1, fontFamily: FONTS.displayMedium, fontSize: 18, color: LINEN }}
          numberOfLines={1}
        >
          {team.name}
        </Text>
        {team.code ? (
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            {team.code}
          </Text>
        ) : null}
      </View>
      <Text
        style={{
          marginTop: 10,
          fontFamily: FONTS.body,
          fontSize: 12,
          color: WS,
        }}
        numberOfLines={2}
      >
        {summarizeScope(team.scope)}
      </Text>
      {team.description ? (
        <Text
          style={{
            marginTop: 8,
            fontFamily: FONTS.bodyLight,
            fontSize: 11.5,
            color: WM,
          }}
          numberOfLines={2}
        >
          {team.description}
        </Text>
      ) : null}
      <Pressable
        onPress={onEdit}
        style={[
          { marginTop: 12, alignSelf: "flex-start" },
          Platform.OS === "web" ? ({ cursor: "pointer" } as unknown as ViewStyle) : null,
        ]}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 11,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: AMBER,
          }}
        >
          Edit →
        </Text>
      </Pressable>
    </View>
  );
}

function RegionSkeletonCard() {
  return (
    <View
      style={{
        flexBasis: ("calc(50% - 6px)" as unknown) as number,
        flexGrow: 1,
        minWidth: 280,
        height: 156,
        padding: 16,
        backgroundColor: TILE_BG,
        borderWidth: 1,
        borderColor: BORDER,
        borderRadius: 4,
        gap: 10,
      }}
    >
      <Skeleton width={140} height={18} />
      <Skeleton width={"70%" as unknown as number} height={11} />
      <Skeleton width={"90%" as unknown as number} height={11} />
      <Skeleton width={60} height={11} />
    </View>
  );
}

/* ─────────────────────────  Region editor modal  ───────────────────────── */

function RegionEditorModal({
  visible,
  companyId,
  initial,
  onClose,
  onSaved,
}: {
  visible: boolean;
  companyId: string;
  initial: RecruitingTeam | null;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [deleteArmed, setDeleteArmed] = useState(false);

  const lastInit = useRef<string | null>(null);
  if (visible) {
    const key = initial?.id ?? "__new__";
    if (lastInit.current !== key) {
      lastInit.current = key;
      setName(initial?.name ?? "");
      setCode(initial?.code ?? "");
      setDescription(initial?.description ?? "");
      setErr(null);
      setDeleteArmed(false);
    }
  } else if (lastInit.current !== null) {
    lastInit.current = null;
  }

  async function save() {
    if (!name.trim()) {
      setErr("Region name is required.");
      return;
    }
    if (name.trim().length > 60) {
      setErr("Region name must be 60 characters or fewer.");
      return;
    }
    setSaving(true);
    setErr(null);
    const result = initial
      ? await updateRecruitingTeam(initial.id, { name, code, description })
      : await createRecruitingTeam(companyId, { name, code, description });
    setSaving(false);
    if (result.error) {
      // Surface unique-constraint conflicts cleanly.
      if (/duplicate|unique/i.test(result.error.message)) {
        setErr("A region with that name already exists.");
      } else {
        setErr(result.error.message);
      }
      return;
    }
    await onSaved();
  }

  async function doDelete() {
    if (!initial) return;
    setSaving(true);
    const { error } = await deleteRecruitingTeam(initial.id);
    setSaving(false);
    if (error) {
      setErr(error.message);
      return;
    }
    await onSaved();
  }

  return (
    <RNModal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.65)",
          justifyContent: "center",
          paddingHorizontal: 16,
        }}
      >
        <View
          style={{
            maxWidth: 520,
            width: "100%",
            maxHeight: "92%",
            alignSelf: "center",
            backgroundColor: FOREST_MID,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: BORDER,
          }}
        >
          <ScrollView contentContainerStyle={{ padding: 22 }}>
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
              {initial ? "Edit region" : "New region"}
            </Text>

            {err ? <InlineError message={err} /> : null}

            <View style={{ marginTop: 16, gap: 14 }}>
              <Labeled label="Region name" required>
                <DarkInput
                  value={name}
                  onChangeText={(v) => setName(v.slice(0, 60))}
                  placeholder="Northeast"
                />
              </Labeled>
              <Labeled label="Region code (optional)">
                <DarkInput
                  value={code}
                  onChangeText={(v) => setCode(v.slice(0, 12))}
                  placeholder="NE"
                  autoCapitalize="characters"
                />
              </Labeled>
              <Labeled label="Description (optional)">
                <DarkInput
                  value={description}
                  onChangeText={(v) => setDescription(v.slice(0, 240))}
                  placeholder="What this region covers"
                  multiline
                />
              </Labeled>
            </View>

            <View
              style={{
                marginTop: 22,
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
              }}
            >
              <View>
                {initial ? (
                  deleteArmed ? (
                    <View style={{ flexDirection: "row", gap: 8, alignItems: "center" }}>
                      <Text style={{ color: WS, fontSize: 11.5, fontFamily: FONTS.body }}>
                        Delete this region?
                      </Text>
                      <DangerButton label="Yes, delete" onPress={doDelete} disabled={saving} />
                      <SecondaryButton label="Cancel" onPress={() => setDeleteArmed(false)} />
                    </View>
                  ) : (
                    <Pressable
                      onPress={() => setDeleteArmed(true)}
                      style={Platform.OS === "web" ? ({ cursor: "pointer" } as unknown as ViewStyle) : undefined}
                    >
                      <Text style={{ color: RED, fontFamily: FONTS.bodyMedium, fontSize: 12 }}>
                        Delete region
                      </Text>
                    </Pressable>
                  )
                ) : null}
              </View>
              <View style={{ flexDirection: "row", gap: 10 }}>
                <SecondaryButton label="Cancel" onPress={onClose} disabled={saving} />
                <PrimaryButton label={saving ? "Saving…" : "Save"} onPress={save} disabled={saving} />
              </View>
            </View>
          </ScrollView>
        </View>
      </View>
    </RNModal>
  );
}

/* ─────────────────────────  Invite teammate modal  ───────────────────────── */

function InviteTeammateModal({
  visible,
  company,
  currentUserName,
  regions,
  onClose,
  onDone,
}: {
  visible: boolean;
  company: Company;
  currentUserName: string | null;
  regions: RecruitingTeam[];
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<CompanyInviteKind>("manager");
  const [regionId, setRegionId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const lastVisible = useRef(false);
  if (visible && !lastVisible.current) {
    lastVisible.current = true;
    setEmail("");
    setRole("manager");
    setRegionId(null);
    setMessage("");
    setErr(null);
    setSuccess(null);
  } else if (!visible && lastVisible.current) {
    lastVisible.current = false;
  }

  async function submit() {
    setErr(null);
    if (!email.trim()) {
      setErr("Email is required.");
      return;
    }
    setSubmitting(true);
    const result = await inviteTeammate({
      email,
      companyId: company.id,
      companyName: company.name,
      kind: role,
      regionId,
      message,
      inviterName: currentUserName ?? undefined,
    });
    setSubmitting(false);
    if (result.mode === "error") {
      setErr(result.error);
      return;
    }
    setSuccess(
      result.mode === "existing_user"
        ? `${email.trim()} already had a Coravu account — they've been added directly.`
        : `Invite sent to ${email.trim()}.`,
    );
    // Hold the success state visible briefly before refresh + close.
    setTimeout(() => {
      void onDone();
    }, 1200);
  }

  return (
    <RNModal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.65)",
          justifyContent: "center",
          paddingHorizontal: 16,
        }}
      >
        <View
          style={{
            maxWidth: 540,
            width: "100%",
            maxHeight: "92%",
            alignSelf: "center",
            backgroundColor: FOREST_MID,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: BORDER,
          }}
        >
          <ScrollView contentContainerStyle={{ padding: 22 }}>
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
              Invite a teammate
            </Text>
            <Text
              style={{
                marginTop: 4,
                fontFamily: FONTS.body,
                fontSize: 12,
                color: WM,
              }}
            >
              They'll get a branded email with a link to set up their account.
            </Text>

            {err ? <InlineError message={err} /> : null}
            {success ? (
              <View
                style={{
                  marginTop: 14,
                  paddingVertical: 10,
                  paddingHorizontal: 12,
                  borderRadius: 6,
                  backgroundColor: "rgba(143,187,138,0.10)",
                  borderWidth: 1,
                  borderColor: "rgba(143,187,138,0.40)",
                }}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: SAGE }}>
                  {success}
                </Text>
              </View>
            ) : null}

            <View style={{ marginTop: 16, gap: 14 }}>
              <Labeled label="Email" required>
                <DarkInput
                  value={email}
                  onChangeText={setEmail}
                  placeholder="teammate@company.com"
                  autoCapitalize="none"
                  keyboardType="email-address"
                />
              </Labeled>
              <Labeled label="Role" required>
                <View style={{ flexDirection: "row", gap: 6, flexWrap: "wrap" }}>
                  {COMPANY_ROLES.map((r) => (
                    <RolePillChoice
                      key={r}
                      label={ROLE_LABEL[r]}
                      active={role === r}
                      onPress={() => setRole(r)}
                    />
                  ))}
                </View>
                <Text
                  style={{
                    marginTop: 8,
                    fontFamily: FONTS.body,
                    fontSize: 11,
                    color: WM,
                    lineHeight: 16,
                  }}
                >
                  {role === "client"
                    ? "Full access — case editing, candidates, judging, billing."
                    : role === "manager"
                      ? "Operates Coravu-managed programs in their assigned region. No billing."
                      : "Scores submissions on cases they're assigned to. No case editing, no billing."}
                </Text>
              </Labeled>
              {regions.length > 0 ? (
                <Labeled label="Region (optional)">
                  <RegionDropdown
                    value={regionId}
                    regions={regions}
                    onChange={setRegionId}
                  />
                </Labeled>
              ) : null}
              <Labeled label="Message (optional)">
                <DarkInput
                  value={message}
                  onChangeText={(v) => setMessage(v.slice(0, 500))}
                  placeholder="A short note that'll appear in the invite email"
                  multiline
                />
              </Labeled>
            </View>

            <View style={{ marginTop: 22, flexDirection: "row", justifyContent: "flex-end", gap: 10 }}>
              <SecondaryButton label="Cancel" onPress={onClose} disabled={submitting} />
              <PrimaryButton
                label={submitting ? "Sending…" : "Send invite"}
                onPress={submit}
                disabled={submitting || !!success}
              />
            </View>
          </ScrollView>
        </View>
      </View>
    </RNModal>
  );
}

function RolePillChoice({
  label,
  active,
  onPress,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={[
        {
          paddingHorizontal: 12,
          paddingVertical: 7,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: active ? AMBER : BORDER,
          backgroundColor: active ? "rgba(181,135,58,0.14)" : "transparent",
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as unknown as ViewStyle) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyMedium,
          fontSize: 12,
          letterSpacing: 0.4,
          color: active ? AMBER : LINEN,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

/* ─────────────────────────  Form primitives  ───────────────────────── */

function Labeled({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <View>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 10,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: WM,
          marginBottom: 6,
        }}
      >
        {label}
        {required ? <Text style={{ color: RED }}> *</Text> : null}
      </Text>
      {children}
    </View>
  );
}

function DarkInput({
  value,
  onChangeText,
  placeholder,
  multiline,
  autoCapitalize,
  keyboardType,
}: {
  value: string;
  onChangeText: (t: string) => void;
  placeholder?: string;
  multiline?: boolean;
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  keyboardType?: "default" | "url" | "email-address";
}) {
  return (
    <TextInput
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor={WM}
      multiline={multiline}
      autoCapitalize={autoCapitalize}
      keyboardType={keyboardType}
      style={
        {
          backgroundColor: FOREST,
          borderWidth: 1,
          borderColor: BORDER,
          borderRadius: 6,
          paddingHorizontal: 12,
          paddingVertical: multiline ? 10 : 9,
          minHeight: multiline ? 80 : undefined,
          textAlignVertical: multiline ? "top" : "center",
          color: LINEN,
          fontFamily: FONTS.body,
          fontSize: 13,
        } as TextStyle
      }
    />
  );
}

function InlineError({ message }: { message: string }) {
  return (
    <View
      style={{
        marginTop: 14,
        paddingVertical: 10,
        paddingHorizontal: 12,
        borderRadius: 6,
        backgroundColor: "rgba(200,101,75,0.12)",
        borderWidth: 1,
        borderColor: "rgba(200,101,75,0.45)",
      }}
    >
      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: RED }}>{message}</Text>
    </View>
  );
}

/* ─────────────────────────  Shared widgets  ───────────────────────── */

function PrimaryAmberButton({
  label,
  onPress,
  disabled,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  const [hover, setHover] = useState(false);
  const web = Platform.OS === "web";
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      {...(web ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const) : {})}
      style={[
        {
          paddingHorizontal: 14,
          paddingVertical: 9,
          borderRadius: 4,
          borderWidth: 1,
          borderColor: AMBER,
          backgroundColor: web && hover && !disabled ? AMBER : "transparent",
          opacity: disabled ? 0.5 : 1,
        } as ViewStyle,
        web ? ({ cursor: disabled ? "default" : "pointer", transition: "all 120ms" } as object) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 10.5,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: web && hover && !disabled ? FOREST : AMBER,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function PrimaryButton({
  label,
  onPress,
  disabled,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <Pressable
      disabled={disabled}
      onPress={onPress}
      style={[
        {
          paddingHorizontal: 16,
          paddingVertical: 9,
          borderRadius: 6,
          backgroundColor: AMBER,
          opacity: disabled ? 0.5 : 1,
        } as ViewStyle,
        Platform.OS === "web"
          ? ({ cursor: disabled ? "default" : "pointer" } as unknown as ViewStyle)
          : null,
      ]}
    >
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: THEME.accentText }}>{label}</Text>
    </Pressable>
  );
}

function SecondaryButton({
  label,
  onPress,
  disabled,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <Pressable
      disabled={disabled}
      onPress={onPress}
      style={[
        {
          paddingHorizontal: 16,
          paddingVertical: 9,
          borderRadius: 6,
          borderWidth: 1,
          borderColor: BORDER,
          backgroundColor: "transparent",
          opacity: disabled ? 0.5 : 1,
        } as ViewStyle,
        Platform.OS === "web"
          ? ({ cursor: disabled ? "default" : "pointer" } as unknown as ViewStyle)
          : null,
      ]}
    >
      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: LINEN }}>{label}</Text>
    </Pressable>
  );
}

function DangerButton({
  label,
  onPress,
  disabled,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <Pressable
      disabled={disabled}
      onPress={onPress}
      style={[
        {
          paddingHorizontal: 16,
          paddingVertical: 9,
          borderRadius: 6,
          borderWidth: 1,
          borderColor: "rgba(200,101,75,0.55)",
          backgroundColor: "rgba(200,101,75,0.15)",
          opacity: disabled ? 0.5 : 1,
        } as ViewStyle,
        Platform.OS === "web"
          ? ({ cursor: disabled ? "default" : "pointer" } as unknown as ViewStyle)
          : null,
      ]}
    >
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: RED }}>{label}</Text>
    </Pressable>
  );
}

function EmptyCard({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <View
      style={{
        padding: 24,
        backgroundColor: TILE_BG,
        borderWidth: 1,
        borderColor: BORDER,
        borderRadius: 4,
        alignItems: "center",
      }}
    >
      <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 18, color: LINEN }}>
        {title}
      </Text>
      {subtitle ? (
        <Text
          style={{
            marginTop: 6,
            fontFamily: FONTS.body,
            fontSize: 12,
            color: WM,
            textAlign: "center",
            maxWidth: 520,
            lineHeight: 18,
          }}
        >
          {subtitle}
        </Text>
      ) : null}
    </View>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <View
      style={{
        marginBottom: 12,
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderWidth: 1,
        borderColor: RED_BORDER,
        backgroundColor: RED_BG,
        borderRadius: 4,
      }}
    >
      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: RED_TEXT }}>
        {message}
      </Text>
    </View>
  );
}

function TeamSkeletonRow() {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingHorizontal: 16,
        paddingVertical: 14,
        backgroundColor: TILE_BG,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: BORDER,
      }}
    >
      <Skeleton width={36} height={36} radius={18} />
      <View style={{ flex: 1 }}>
        <Skeleton width={"50%" as unknown as number} height={13} />
        <Skeleton width={"75%" as unknown as number} height={11} style={{ marginTop: 8 }} />
      </View>
      <Skeleton width={60} height={20} radius={999} />
    </View>
  );
}

void W;
void WS;
void WL;
